import React, { useState, useContext } from 'react';
import { getAuth, signInWithEmailAndPassword } from 'firebase/auth';
import { app } from '../../firebase';
import { StudentContext } from '../../contexts/StudentContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useNavigate } from 'react-router-dom';
import { BookOpen } from 'lucide-react';

function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { setStudentData } = useContext(StudentContext);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!email.trim() || !password.trim()) {
      setError('Please fill in all fields');
      setLoading(false);
      return;
    }
    
    const auth = getAuth(app);
  
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      //console.log('Authenticated User UID:', user.uid);
      
      const studentRef = doc(db, 'students', user.uid);
      const studentSnap = await getDoc(studentRef);
      
      if (studentSnap.exists()) {
        const userData = { uid: user.uid, id: user.uid, ...studentSnap.data() };
        setStudentData(userData);
        //console.log('Student data set:', userData);
        
        navigate('/');
      } else {
        throw new Error('Student record not found. Please contact support.');
      }
    } catch (err) {
      console.error('Login error:', err);
      switch (err.code) {
        case 'auth/invalid-email':
        case 'auth/user-not-found':
        case 'auth/wrong-password':
          setError('Incorrect email or password');
          break;
        case 'auth/too-many-requests':
          setError('Too many attempts. Please try again later.');
          break;
        default:
          setError('An error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = () => {
    navigate('/forgot-password', { state: { email: email } });
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div
      style={{
        backgroundColor: '#f6f9fc',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <style>
        {`
          .login-card {
            max-width: 450px;
            width: 100%;
            border-radius: 12px;
            box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
            background-color: #fff;
            padding: 2.5rem;
          }
          .form-label {
            font-size: 1rem;
            color: #666;
            margin-bottom: 0.75rem;
          }
          .form-control {
            border: 1px solid #e0e0e0;
            border-radius: 0;
            padding: 0.9rem;
            font-size: 1.1rem;
            color: #333;
            width: 100%;
            box-sizing: border-box;
            transition: all 0.2s ease;
          }
          .form-control:focus {
            border-color: #007bff;
            box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
            outline: none;
          }
          .form-check-label {
            font-size: 1rem;
            color: #666;
          }
          .btn-primary {
            background-color: #007bff;
            border: none;
            border-radius: 6px;
            padding: 0.9rem;
            font-size: 1.1rem;
            font-weight: 500;
            width: 100%;
            color: #fff;
            cursor: pointer;
          }
          .btn-primary:disabled {
            background-color: #66a3ff;
            cursor: not-allowed;
          }
          .link {
            font-size: 1rem;
            color: #007bff;
            text-decoration: none;
            background: none;
            border: none;
            padding: 0;
            cursor: pointer;
          }
          .link:hover {
            text-decoration: underline;
          }
          .error-message {
            font-size: 1rem;
            color: #dc3545;
            margin-bottom: 1.25rem;
          }
          .input-group {
            display: flex;
            align-items: center;
            width: 100%;
            border: 1px solid #e0e0e0;
            border-radius: 8px;
            overflow: hidden;
          }
          .input-group-text {
            background-color: #f9f9f9;
            border: none;
            cursor: pointer;
            padding: 0.9rem;
            display: flex;
            align-items: center;
          }
          .input-group-text i {
            font-size: 18px;
            color: #000;
          }
          .input-group-text svg {
            width: 18px;
            height: 18px;
          }
          .logo-container {
            display: flex;
            align-items: center;
            justify-content: center;
            margin-right: 0.75rem;
          }
          .logo-text {
            font-size: 1.4rem;
            font-weight: 700;
            color: #111827;
          }
          .flex-container {
            display: flex;
            align-items: center;
            justify-content: center;
            margin-right: 0.75rem;
          }
          .text-center {
            text-align: center;
            margin-bottom: 1.75rem;
          }
          .mb-2 {
            margin-bottom: 0.5rem;
          }
          .mt-2 {
            margin-top: 0.5rem;
          }
          .mb-0 {
            margin-bottom: 0;
          }
          .mb-4 {
            margin-bottom: 1rem;
          }
          .mt-3 {
            margin-top: 0.75rem;
          }
          .d-flex {
            display: flex;
          }
          .justify-content-between {
            justify-content: space-between;
          }
          .align-items-center {
            align-items: center;
          }
          .form-check {
            display: flex;
            align-items: center;
          }
          .form-check-input {
            margin-right: 0.5rem;
          }

          /* Mobile Responsiveness */
          @media (max-width: 767px) {
            .login-card {
              max-width: 90%; /* Adjust for mobile */
              padding: 1.5rem; /* Reduce padding */
            }
            .form-label {
              font-size: 0.9rem; /* Slightly smaller */
            }
            .form-control {
              padding: 0.8rem; /* Slightly smaller but still touch-friendly */
              font-size: 1rem; /* Smaller font */
            }
            .btn-primary {
              padding: 0.8rem;
              font-size: 1rem;
            }
            .link {
              font-size: 0.9rem;
            }
            .error-message {
              font-size: 0.9rem;
              margin-bottom: 1rem;
            }
            .input-group-text {
              padding: 0.8rem;
            }
            .input-group-text i {
              font-size: 16px; /* Smaller icon */
            }
            .input-group-text svg {
              width: 16px;
              height: 16px;
            }
            .logo-container {
              margin-right: 0.5rem;
            }
            .logo-text {
              font-size: 1.2rem; /* Smaller logo text */
            }
            .text-center {
              margin-bottom: 1.25rem;
            }
            .mb-4 {
              margin-bottom: 0.75rem; /* Reduce spacing */
            }
            .mt-3 {
              margin-top: 0.5rem;
            }
            .d-flex {
              flex-direction: column; /* Stack elements vertically */
              gap: 0.5rem; /* Add spacing between stacked elements */
            }
            .justify-content-between {
              justify-content: center; /* Center stacked elements */
            }
            .form-check {
              justify-content: center;
            }
          }
        `}
      </style>
      <div className="text-center">
        <div className="flex-container">
          <div
            className="logo-container"
            style={{
              width: '2.25rem',
              height: '2.25rem',
              background: 'linear-gradient(to right, #2563eb, #10b981)',
              borderRadius: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <BookOpen style={{ width: '1.35rem', height: '1.35rem', color: '#fff' }} />
          </div>
          <span className="logo-text">InternSkool</span>
        </div>
        <h1 className="mb-2 mt-2" style={{ fontWeight: '600', color: '#333', fontSize: '1.75rem' }}>
          Welcome back
        </h1>
        <p className="mb-0" style={{ color: '#666', fontSize: '1.1rem' }}>
          Sign in to your InternSkool account
        </p>
      </div>
      <div className="login-card">
        <div>
          <div className="mb-4">
            <label htmlFor="email" className="form-label">
              Email Address
            </label>
            <div className="input-group">
              <span className="input-group-text">
                <i className="bi bi-envelope-at-fill"></i>
              </span>
              <input
                id="email"
                type="email"
                className="form-control"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                disabled={loading}
                style={{ border: 'none' }}
              />
              <span className="input-group-text" style={{ visibility: 'hidden' }}></span>
            </div>
          </div>
          <div className="mb-4">
            <label htmlFor="password" className="form-label">
              Password
            </label>
            <div className="input-group">
              <span className="input-group-text">
                <i className="bi bi-lock-fill"></i>
              </span>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                className="form-control"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                disabled={loading}
                style={{ border: 'none' }}
              />
              <span className="input-group-text" onClick={togglePasswordVisibility}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="#666" className={showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'} viewBox="0 0 16 16">
                  {showPassword ? (
                    <path d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7.028 7.028 0 0 0-2.79.588l.77.771A5.944 5.944 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.134 13.134 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755-.165.165-.337.328-.517.486l.708.709zM8 4.5c-.725 0-1.391.222-1.945.588l.769.77A2.97 2.97 0 0 1 8 5.5c1.654 0 3 1.346 3 3 0 .157-.013.312-.038.465l.77.77A2.95 2.95 0 0 0 11 8.5c0-1.654-1.346-3-3-3zm-4.828 3C2.168 6.663 1.5 7.5 1.5 8s.5 1.5 1.5 1.5c.172 0 .344-.025.512-.075l-.77-.77c-.038.013-.077.025-.117.037-.335.088-.83-.088-1.465-.755-.073-.105-.137-.201-.195-.288A13.134 13.134 0 0 1 1.172 8c-.058-.087-.122-.183-.195-.288-.335-.48-.83-1.12-1.465-1.755C1.168 5.163 2.5 4.5 4 4.5c.344 0 .688.038 1.025.113l-.77.77A5.944 5.944 0 0 0 4 5.5c-.725 0-1.391.222-1.945.588l.769.77A2.97 2.97 0 0 1 4 6.5c1.654 0 3 1.346 3 3 0 .157-.013.312-.038.465l.77.77A2.95 2.95 0 0 0 7 8.5c0-1.654-1.346-3-3-3zm-1.465 3.755l-.77-.77c-.038.013-.077.025-.117.037-.335.088-.83-.088-1.465-.755-.073-.105-.137-.201-.195-.288A13.134 13.134 0 0 1 1.172 8c-.058-.087-.122-.183-.195-.288-.335-.48-.83-1.12-1.465-1.755C1.168 5.163 2.5 4.5 4 4.5c.344 0 .688.038 1.025.113l-.77.77z"/>
                  ) : (
                    <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z"/>
                  )}
                  {showPassword ? (
                    <path d="M2.146 2.854a.5.5 0 1 1 .708-.708l11 11a.5.5 0 0 1-.708.708l-11-11z"/>
                  ) : (
                    <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z"/>
                  )}
                </svg>
              </span>
            </div>
          </div>
          {error && <div className="error-message">{error}</div>}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="rememberMe"
                disabled={loading}
              />
              <label className="form-check-label" htmlFor="rememberMe">
                Remember me
              </label>
            </div>
            
            <button
              type="button"
              className="link"
              onClick={handleForgotPassword}
              disabled={loading}
            >
              Forgot password?
            </button>
          </div>
          <button
            type="submit"
            className="btn-primary"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? (
              <>
                <span style={{ marginRight: '0.5rem' }} className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                Signing In...
              </>
            ) : (
              'Sign In'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
